import { useRef, useEffect, useState } from "react";
import { Card, Tag, Typography, Button, Space, Empty, Tooltip } from "antd";
import {
  ClearOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import type { MqttMessage } from "../hooks/useMqttClient";

const { Text } = Typography;

interface MqttMessageLogProps {
  messages: MqttMessage[];
  onClear?: () => void;
  maxHeight?: number;
  height?: number;
}

function formatTime(d: Date) {
  return d.toLocaleTimeString("zh-CN", { hour12: false });
}

function topicLabel(topic: string): { label: string; color: string } {
  if (topic.endsWith("/commands/set"))
    return { label: "下发指令", color: "blue" };
  if (topic.endsWith("/properties/report"))
    return { label: "设备上报", color: "green" };
  return { label: topic.split("/").slice(-2).join("/"), color: "default" };
}

export function MqttMessageLog({
  messages,
  onClear,
  maxHeight = 240,
  height,
}: MqttMessageLogProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  useEffect(() => {
    if (autoScroll && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages.length, autoScroll]);

  return (
    <Card
      size="small"
      title={
        <Space>
          <Text strong>MQTT 实时消息</Text>
          <Tag>{messages.length} 条</Tag>
        </Space>
      }
      extra={
        <Space>
          <Tooltip title={autoScroll ? "暂停滚动" : "自动滚动"}>
            <Button
              size="small"
              type="text"
              icon={
                autoScroll ? <PauseCircleOutlined /> : <PlayCircleOutlined />
              }
              onClick={() => setAutoScroll(!autoScroll)}
            />
          </Tooltip>
          {messages.length > 0 && onClear && (
            <Button size="small" icon={<ClearOutlined />} onClick={onClear}>
              清空
            </Button>
          )}
        </Space>
      }
    >
      <div
        ref={scrollRef}
        style={{
          maxHeight,
          height,
          overflow: "auto",
          background: "#fafafa",
          borderRadius: 6,
          padding: "4px 0",
        }}
      >
        {messages.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="暂无 MQTT 消息"
            style={{ margin: "24px 0" }}
          />
        ) : (
          messages.map((m) => {
            const { label, color } = topicLabel(m.topic);
            return (
              <div
                key={m.id}
                style={{
                  padding: "6px 12px",
                  borderBottom: "1px solid #f0f0f0",
                  fontSize: 12,
                }}
              >
                <Space size={8}>
                  <Text code style={{ fontSize: 11, whiteSpace: "nowrap" }}>
                    {formatTime(m.timestamp)}
                  </Text>
                  <Tag color={color} style={{ fontSize: 10, margin: 0 }}>
                    {label}
                  </Tag>
                </Space>
                <pre
                  style={{
                    margin: "4px 0 0 0",
                    fontSize: 10,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-all",
                    color: "#555",
                    lineHeight: 1.2,
                  }}
                >
                  {JSON.stringify(m.payload)}
                </pre>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}
